import { useMemo, useState } from 'react'
import { confirmRenewed, deleteRecord, rdapRefresh, type ApiError } from './api'
import { daysUntil, groupOf, phrase, searchText, tone } from './model'
import type { Rec, Section as Sec } from './model'
import { CopyButton, Pill } from './ui'
import { SecretRow } from './SecretRow'
import { toast } from './toast'

const s = (v: unknown) => (v == null ? '' : String(v))

type DateFilter = 'all' | 'over' | 'soon' | 'none'

export function Section({
  section,
  rows,
  selectedId,
  onSelect,
  filter,
  onFilter,
  query,
  canEdit,
  onEdit,
  onChanged,
}: {
  section: Sec
  rows: Rec[]
  selectedId: string | null
  onSelect: (id: string | null) => void
  filter: DateFilter
  onFilter: (f: DateFilter) => void
  query: string
  canEdit: boolean
  onEdit: (rec: Rec) => void
  onChanged: () => void
}) {
  const dateField = section.dateField

  const list = useMemo(() => {
    let r: (Rec & { _n: number | null })[] = rows.map((x) => ({
      ...x,
      _n: dateField ? daysUntil(x[dateField]) : null,
    }))
    if (query) {
      const q = query.toLowerCase()
      r = r.filter((x) => searchText(x).includes(q))
    }
    if (dateField && filter !== 'all') {
      r = r.filter((x) =>
        filter === 'over'
          ? x._n !== null && x._n < 0
          : filter === 'soon'
            ? x._n !== null && x._n >= 0 && x._n <= 45
            : x._n === null,
      )
    }
    if (dateField) {
      r.sort((a, b) => (a._n ?? 9e9) - (b._n ?? 9e9))
    } else {
      r.sort((a, b) => s(a[section.titleField]).localeCompare(s(b[section.titleField])))
    }
    return r
  }, [rows, dateField, filter, query, section.titleField])

  const selected = selectedId ? rows.find((r) => s(r.id) === selectedId) ?? null : null

  return (
    <div className={`pane${selected ? ' split' : ''}`}>
      <div className="listcol">
        {dateField && (
          <div className="chips">
            {(
              [
                ['all', 'All'],
                ['over', 'Past due'],
                ['soon', 'Next 45 days'],
                ['none', 'No date recorded'],
              ] as [DateFilter, string][]
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                className="chip"
                aria-pressed={filter === k}
                onClick={() => onFilter(k)}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {list.length === 0 ? (
          <p className="empty">
            {query ? `Nothing matches “${query}”.` : 'Nothing in this section.'}
          </p>
        ) : (
          <RowList
            list={list}
            section={section}
            dateField={dateField}
            selectedId={selectedId}
            onSelect={onSelect}
          />
        )}
      </div>

      {selected && (
        <Detail
          key={s(selected.id)}
          section={section}
          rec={selected}
          onClose={() => onSelect(null)}
          canEdit={canEdit}
          onEdit={() => onEdit(selected)}
          onChanged={onChanged}
        />
      )}
    </div>
  )
}

function RowList({
  list,
  section,
  dateField,
  selectedId,
  onSelect,
}: {
  list: (Rec & { _n: number | null })[]
  section: Sec
  dateField?: string
  selectedId: string | null
  onSelect: (id: string) => void
}) {
  const out: React.ReactNode[] = []
  let group = ''
  for (const x of list) {
    if (dateField) {
      const g = groupOf(x._n)
      if (g !== group) {
        group = g
        out.push(
          <div className="grp" key={`g-${g}`}>
            {g}
          </div>,
        )
      }
    }
    const flag = section.flag ? section.flag(x) : ''
    out.push(
      <button
        type="button"
        className="row"
        key={s(x.id)}
        aria-selected={selectedId === s(x.id)}
        onClick={() => onSelect(s(x.id))}
      >
        <div className="main">
          <div className="t">{s(x[section.titleField]) || 'Untitled'}</div>
          <div className="m">{section.meta(x) || ' '}</div>
        </div>
        {flag && <Pill tone="amber">{flag}</Pill>}
        {dateField && <Pill tone={tone(x._n)}>{phrase(x._n)}</Pill>}
      </button>,
    )
  }
  return <>{out}</>
}

function Detail({
  section,
  rec,
  onClose,
  canEdit,
  onEdit,
  onChanged,
}: {
  section: Sec
  rec: Rec
  onClose: () => void
  canEdit: boolean
  onEdit: () => void
  onChanged: () => void
}) {
  const n = section.dateField ? daysUntil(rec[section.dateField]) : null
  const url = section.link ? section.link(rec) : ''
  const flag = section.flag ? section.flag(rec) : ''
  const dated = ['domains', 'hosting', 'subscriptions'].includes(section.key)

  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [busy, setBusy] = useState(false)

  async function doConfirmRenewed() {
    setBusy(true)
    try {
      await confirmRenewed(section.key, s(rec.id))
      toast('Marked as renewed today')
      onChanged()
    } catch (e) {
      toast((e as ApiError).message ?? 'Could not update')
    } finally {
      setBusy(false)
    }
  }

  async function doRdap() {
    setBusy(true)
    try {
      const r = await rdapRefresh(s(rec.id))
      toast(
        r.to === null
          ? `No public RDAP data for ${r.name}`
          : r.changed
            ? `Expiry updated: ${r.from ?? '—'} → ${r.to}`
            : `Expiry confirmed: ${r.to}`,
      )
      onChanged()
    } catch (e) {
      toast((e as ApiError).message ?? 'RDAP lookup failed')
    } finally {
      setBusy(false)
    }
  }

  async function doDelete() {
    setBusy(true)
    try {
      await deleteRecord(section.key, s(rec.id))
      toast('Deleted')
      onChanged()
      onClose()
    } catch (e) {
      toast((e as ApiError).message ?? 'Could not delete')
      setBusy(false)
      setConfirmingDelete(false)
    }
  }

  const fmt = (f: { key: string; kind?: string }) => {
    const raw = rec[f.key]
    if (raw == null || raw === '') return { text: 'Not set', none: true }
    if (f.kind === 'bool') return { text: raw ? 'On' : 'Off', none: false }
    if (f.kind === 'date') {
      const d = daysUntil(raw)
      return { text: `${raw}${d === null ? '' : `  ·  ${phrase(d)}`}`, none: false }
    }
    return { text: String(raw), none: false }
  }

  return (
    <div className="detail">
      <div className="dhead">
        <button className="xb" title="Close" onClick={onClose}>
          &times;
        </button>
        <h2>{s(rec[section.titleField]) || 'Untitled'}</h2>
        <p className="sub">
          {section.dateField
            ? `${phrase(n)}  ·  ${s(rec[section.dateField]) || 'no date recorded'}`
            : section.label}
        </p>
      </div>

      <div className="acts">
        {url && (
          <a className="btn s" href={url} target="_blank" rel="noopener noreferrer">
            Open
          </a>
        )}
        {canEdit && (
          <button type="button" className="btn s" onClick={onEdit} disabled={busy}>
            Edit
          </button>
        )}
        {canEdit && dated && (
          <button type="button" className="btn s" onClick={doConfirmRenewed} disabled={busy}>
            Confirm renewed
          </button>
        )}
        {canEdit && section.key === 'domains' && (
          <button type="button" className="btn s" onClick={doRdap} disabled={busy}>
            Check expiry (RDAP)
          </button>
        )}
        {canEdit &&
          (confirmingDelete ? (
            <>
              <button type="button" className="btn s danger" onClick={doDelete} disabled={busy}>
                {busy ? 'Deleting…' : 'Really delete'}
              </button>
              <button
                type="button"
                className="btn s"
                onClick={() => setConfirmingDelete(false)}
                disabled={busy}
              >
                Keep
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn s danger"
              onClick={() => setConfirmingDelete(true)}
              disabled={busy}
            >
              Delete
            </button>
          ))}
      </div>

      {section.fields.map((f) => {
        const v = fmt(f)
        const copyable = !v.none && f.kind !== 'date' && f.kind !== 'bool'
        return (
          <div className="kv" key={f.key}>
            <span className="k">{f.label}</span>
            <span className={`v${v.none ? ' none' : ''}`}>{v.text}</span>
            {copyable && <CopyButton value={String(rec[f.key])} />}
          </div>
        )
      })}

      {section.secret && (
        <SecretRow section={section} rec={rec} canEdit={canEdit} onChanged={onChanged} />
      )}

      {flag && (
        <p className="hint">
          {flag === 'Personal email login' || flag === 'Personal account'
            ? 'This is a company asset on a personal login — it leaves with the person. Worth moving to a company account.'
            : flag === 'No one assigned'
              ? 'No person or team is recorded against this number.'
              : flag}
        </p>
      )}

      {!canEdit && (
        <p className="hint">You have view access. Ask an administrator to change records.</p>
      )}
    </div>
  )
}
